import { useState, useCallback } from 'react';
import { useViewport } from 'reactflow';
import { useNode } from '@/components/Recipe/FlowContext';
import type { NodeId } from 'web';

interface UseAvoidNodeOverflowOnPasteParams {
  nodeId: NodeId;
  padding?: number;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

export function useAvoidNodeOverflowOnPaste({ nodeId, textareaRef }: UseAvoidNodeOverflowOnPasteParams) {
  const { zoom, y: viewportY } = useViewport();
  const node = useNode(nodeId);
  const [shouldUseMaxHeight, setShouldUseMaxHeight] = useState(false);

  // Convert node position from React Flow space to viewport/screen space
  const nodeYInViewport = node.position.y * zoom + viewportY;

  // Calculate available space from node position to bottom of screen
  const availableSpaceBelow = window.innerHeight - nodeYInViewport;

  // Convert back to node space by dividing by zoom
  const calculatedMaxHeight = availableSpaceBelow / zoom;

  const onPaste = useCallback(() => {
    setShouldUseMaxHeight(true);

    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (textarea) {
        // Move cursor to the beginning
        textarea.setSelectionRange(0, 0);
        // Scroll to top
        textarea.scrollTop = 0;
      }
      setShouldUseMaxHeight(false);
    });
  }, [textareaRef]);

  return {
    maxHeight: shouldUseMaxHeight ? calculatedMaxHeight : undefined,
    onPaste,
  };
}
